import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Input,
  Button,
  Spin,
  Space,
  Divider,
  Tag,
  Progress,
  List,
  Alert,
  Collapse,
  Statistic,
  Tooltip,
  Badge,
  Empty,
  Tabs,
} from 'antd';
import {
  SearchOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  QuestionCircleOutlined,
  LinkOutlined,
  BookOutlined,
  BarChartOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  BulbOutlined,
  ExclamationCircleOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { API_SERVIDOR2 } from '../config/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;
const { TabPane } = Tabs;

interface Acordao {
  numero_processo: string;
  resultado_julgamento: string;
  turma: string;
  votacao: string;
  data_sessao: string;
  tributo: string;
  link_pdf: string;
  ementa: string;
}

interface AnaliseResponse {
  pergunta: string;
  resposta: string;
  confianca: {
    score: number;
    nivel: string;
    detalhes: {
      fontes: number;
      decisoes_favoraveis: number;
      decisoes_desfavoraveis: number;
      decisoes_recentes_2024_2025: number;
    };
  };
  divergencia: {
    tem_divergencia: boolean;
    turmas_favoraveis: string[];
    turmas_desfavoraveis: string[];
  };
  acordaos: Acordao[];
  total_encontrados: number;
}

interface PredicaoResponse {
  predicao: string;
  confianca: string;
  estatisticas: {
    casos_analisados: number;
    favoraveis: number;
    desfavoraveis: number;
    parcialmente_providos: number;
    taxa_sucesso: number;
    taxa_sucesso_2024_2025: number;
    decisoes_unanimes_favoraveis: number;
    decisoes_voto_qualidade: number;
  };
  tramitacao: {
    processos_similares_pendentes: number;
    tempo_medio_anos: number | string;
  };
  legislacao_aplicavel: Array<{
    tema: string;
    lei: string;
    sumula_carf?: string;
    observacao: string;
  }>;
  recomendacao: string;
}

const JuridicoPage: React.FC = () => {
  const [pergunta, setPergunta] = useState('');
  const [tributo, setTributo] = useState('');
  const [tese, setTese] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingPredicao, setLoadingPredicao] = useState(false);
  const [analise, setAnalise] = useState<AnaliseResponse | null>(null);
  const [predicao, setPredicao] = useState<PredicaoResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('consulta');

  const buscarJurisprudencia = async () => {
    if (!pergunta.trim()) return;
    
    setLoading(true);
    setError(null);
    setAnalise(null);
    
    try {
      const response = await axios.post(API_SERVIDOR2.IA_JURIDICO_V2, {
        pergunta: pergunta.trim(),
        limite_acordaos: 15,
      });
      setAnalise(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao buscar jurisprudência');
    } finally {
      setLoading(false);
    }
  };

  const buscarPredicao = async () => {
    if (!tributo.trim() || !tese.trim()) return;
    
    setLoadingPredicao(true);
    setError(null);
    setPredicao(null);
    
    try {
      const response = await axios.post(API_SERVIDOR2.IA_PREDICAO, {
        tributo: tributo.trim(),
        tese: tese.trim(),
      });
      setPredicao(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao buscar predição');
    } finally {
      setLoadingPredicao(false);
    }
  };

  const getConfiancaColor = (score: number) => {
    if (score >= 75) return '#52c41a';
    if (score >= 50) return '#faad14';
    return '#f5222d';
  };

  const getPredicaoTag = (predicao: string) => {
    switch (predicao) {
      case 'favoravel':
        return <Tag color="success" icon={<CheckCircleOutlined />}>FAVORÁVEL</Tag>;
      case 'desfavoravel':
        return <Tag color="error" icon={<CloseCircleOutlined />}>DESFAVORÁVEL</Tag>;
      default:
        return <Tag color="warning" icon={<QuestionCircleOutlined />}>INCERTO</Tag>;
    }
  };

  const getResultadoTag = (resultado: string) => {
    if (resultado?.includes('Negado')) {
      return <Tag color="red">Negado</Tag>;
    }
    if (resultado?.includes('Provido') && !resultado?.includes('Negado')) {
      return <Tag color="green">Provido</Tag>;
    }
    return <Tag color="orange">Parcial</Tag>;
  };

  return (
    <div style={{ padding: '24px', maxWidth: 1400, margin: '0 auto' }}>
      <Title level={2}>
        <BookOutlined /> Consulta Jurídica CARF
      </Title>
      <Text type="secondary">
        IA jurídica com 65.352 acórdãos (2022-2025) + 800.000 processos em tramitação
      </Text>

      <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ marginTop: 24 }}>
        <TabPane
          tab={<span><SearchOutlined /> Consulta RAG</span>}
          key="consulta"
        >
          <Card style={{ marginBottom: 24 }}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <div>
                <Text strong>Faça sua pergunta sobre jurisprudência tributária:</Text>
                <TextArea
                  rows={3}
                  placeholder="Ex: A prescrição intercorrente se aplica no processo administrativo fiscal?"
                  value={pergunta}
                  onChange={(e) => setPergunta(e.target.value)}
                  style={{ marginTop: 8 }}
                  onPressEnter={(e) => {
                    if (e.ctrlKey) buscarJurisprudencia();
                  }}
                />
              </div>
              <Button
                type="primary"
                icon={<RobotOutlined />}
                onClick={buscarJurisprudencia}
                loading={loading}
                size="large"
              >
                Consultar Jurisprudência
              </Button>
            </Space>
          </Card>

          {error && (
            <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />
          )}

          {loading && (
            <Card>
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>Analisando jurisprudência do CARF...</div>
              </div>
            </Card>
          )}

          {analise && (
            <>
              {/* Score de Confiança */}
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} md={8}>
                  <Card>
                    <Statistic
                      title="Score de Confiança"
                      value={analise.confianca.score}
                      suffix="/ 100"
                      valueStyle={{ color: getConfiancaColor(analise.confianca.score) }}
                      prefix={
                        analise.confianca.score >= 75 ? <CheckCircleOutlined /> :
                        analise.confianca.score >= 50 ? <ExclamationCircleOutlined /> :
                        <CloseCircleOutlined />
                      }
                    />
                    <Progress
                      percent={analise.confianca.score}
                      strokeColor={getConfiancaColor(analise.confianca.score)}
                      showInfo={false}
                      style={{ marginTop: 8 }}
                    />
                    <Tag color={
                      analise.confianca.nivel === 'Alta' ? 'green' :
                      analise.confianca.nivel === 'Média' ? 'orange' : 'red'
                    } style={{ marginTop: 8 }}>
                      Confiança {analise.confianca.nivel}
                    </Tag>
                  </Card>
                </Col>
                <Col xs={24} md={8}>
                  <Card>
                    <Statistic
                      title="Decisões Analisadas"
                      value={analise.confianca.detalhes.fontes}
                      prefix={<FileTextOutlined />}
                    />
                    <div style={{ marginTop: 8 }}>
                      <Tag color="green">{analise.confianca.detalhes.decisoes_favoraveis} favoráveis</Tag>
                      <Tag color="red">{analise.confianca.detalhes.decisoes_desfavoraveis} desfavoráveis</Tag>
                    </div>
                  </Card>
                </Col>
                <Col xs={24} md={8}>
                  <Card>
                    <Statistic
                      title="Divergência entre Turmas"
                      value={analise.divergencia.tem_divergencia ? 'SIM' : 'NÃO'}
                      valueStyle={{ 
                        color: analise.divergencia.tem_divergencia ? '#f5222d' : '#52c41a' 
                      }}
                      prefix={analise.divergencia.tem_divergencia ? 
                        <ExclamationCircleOutlined /> : <CheckCircleOutlined />
                      }
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {analise.confianca.detalhes.decisoes_recentes_2024_2025} decisões em 2024/2025
                    </Text>
                  </Card>
                </Col>
              </Row>

              {/* Resposta da IA */}
              <Card 
                title={<><BulbOutlined /> Análise da IA</>}
                style={{ marginBottom: 24 }}
              >
                <Paragraph style={{ whiteSpace: 'pre-wrap', fontSize: 15 }}>
                  {analise.resposta}
                </Paragraph>
              </Card>

              {/* Acórdãos */}
              <Card title={<><FileTextOutlined /> Acórdãos Relevantes ({analise.total_encontrados} encontrados)</>}>
                <List
                  dataSource={analise.acordaos}
                  renderItem={(acordao, index) => (
                    <List.Item
                      key={index}
                      actions={[
                        acordao.link_pdf && (
                          <Button
                            type="link"
                            icon={<LinkOutlined />}
                            href={acordao.link_pdf}
                            target="_blank"
                          >
                            PDF
                          </Button>
                        )
                      ]}
                    >
                      <List.Item.Meta
                        title={
                          <Space>
                            <Text strong>Processo {acordao.numero_processo || `#${index + 1}`}</Text>
                            {getResultadoTag(acordao.resultado_julgamento)}
                            <Tag>{acordao.votacao}</Tag>
                            <Tag color="blue">{acordao.tributo}</Tag>
                          </Space>
                        }
                        description={
                          <>
                            <Text type="secondary">{acordao.turma} | {acordao.data_sessao}</Text>
                            <Paragraph 
                              ellipsis={{ rows: 2, expandable: true }}
                              style={{ marginTop: 8, marginBottom: 0 }}
                            >
                              {acordao.ementa}
                            </Paragraph>
                          </>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Card>
            </>
          )}
        </TabPane>

        <TabPane
          tab={<span><BarChartOutlined /> Predição de Resultado</span>}
          key="predicao"
        >
          <Card style={{ marginBottom: 24 }}>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Text strong>Tributo:</Text>
                <Input
                  placeholder="Ex: IRPJ, CSLL, PIS, COFINS, IPI"
                  value={tributo}
                  onChange={(e) => setTributo(e.target.value)}
                  style={{ marginTop: 8 }}
                />
              </Col>
              <Col xs={24} md={12}>
                <Text strong>Tese/Matéria:</Text>
                <Input
                  placeholder="Ex: responsabilidade sócios dissolução irregular"
                  value={tese}
                  onChange={(e) => setTese(e.target.value)}
                  style={{ marginTop: 8 }}
                />
              </Col>
            </Row>
            <Button
              type="primary"
              icon={<ThunderboltOutlined />}
              onClick={buscarPredicao}
              loading={loadingPredicao}
              size="large"
              style={{ marginTop: 16 }}
            >
              Prever Resultado
            </Button>
          </Card>

          {loadingPredicao && (
            <Card>
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>Analisando casos similares...</div>
              </div>
            </Card>
          )}

          {predicao && (
            <>
              {/* Resultado da Predição */}
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} md={8}>
                  <Card>
                    <div style={{ textAlign: 'center' }}>
                      <Title level={4}>Predição</Title>
                      <div style={{ fontSize: 24, marginBottom: 8 }}>
                        {getPredicaoTag(predicao.predicao)}
                      </div>
                      <Tag color={predicao.confianca === 'alta' ? 'green' : 'orange'}>
                        Confiança {predicao.confianca}
                      </Tag>
                    </div>
                  </Card>
                </Col>
                <Col xs={24} md={8}>
                  <Card>
                    <Statistic
                      title="Taxa de Sucesso"
                      value={predicao.estatisticas.taxa_sucesso}
                      suffix="%"
                      precision={1}
                      valueStyle={{ 
                        color: predicao.estatisticas.taxa_sucesso >= 50 ? '#52c41a' : '#f5222d' 
                      }}
                    />
                    <Progress 
                      percent={predicao.estatisticas.taxa_sucesso} 
                      strokeColor={predicao.estatisticas.taxa_sucesso >= 50 ? '#52c41a' : '#f5222d'}
                      showInfo={false}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      2024/2025: {predicao.estatisticas.taxa_sucesso_2024_2025}%
                    </Text>
                  </Card>
                </Col>
                <Col xs={24} md={8}>
                  <Card>
                    <Statistic
                      title="Casos Analisados"
                      value={predicao.estatisticas.casos_analisados}
                      prefix={<FileTextOutlined />}
                    />
                    <div style={{ marginTop: 8 }}>
                      <Tag color="green">{predicao.estatisticas.favoraveis} favoráveis</Tag>
                      <Tag color="red">{predicao.estatisticas.desfavoraveis} contra</Tag>
                    </div>
                  </Card>
                </Col>
              </Row>

              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} md={12}>
                  <Card title={<><ClockCircleOutlined /> Tramitação</>} size="small">
                    <Statistic
                      title="Tempo Médio"
                      value={predicao.tramitacao.tempo_medio_anos}
                      suffix="anos"
                    />
                    <Divider style={{ margin: '12px 0' }} />
                    <Statistic
                      title="Processos Similares Pendentes"
                      value={predicao.tramitacao.processos_similares_pendentes}
                    />
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card title={<><BarChartOutlined /> Detalhes Votação</>} size="small">
                    <Space direction="vertical">
                      <div>
                        <Text>Unânimes favoráveis: </Text>
                        <Badge count={predicao.estatisticas.decisoes_unanimes_favoraveis} 
                               style={{ backgroundColor: '#52c41a' }} />
                      </div>
                      <div>
                        <Text>Voto de qualidade: </Text>
                        <Badge count={predicao.estatisticas.decisoes_voto_qualidade}
                               style={{ backgroundColor: '#faad14' }} />
                        {predicao.estatisticas.decisoes_voto_qualidade > 0 && (
                          <Tooltip title="Matéria controvertida - decisões apertadas">
                            <ExclamationCircleOutlined style={{ marginLeft: 8, color: '#faad14' }} />
                          </Tooltip>
                        )}
                      </div>
                      <div>
                        <Text>Parcialmente providos: </Text>
                        <Badge count={predicao.estatisticas.parcialmente_providos}
                               style={{ backgroundColor: '#1890ff' }} />
                      </div>
                    </Space>
                  </Card>
                </Col>
              </Row>

              {/* Legislação Aplicável */}
              {predicao.legislacao_aplicavel.length > 0 && (
                <Card 
                  title={<><BookOutlined /> Legislação Aplicável</>}
                  style={{ marginBottom: 24 }}
                >
                  <List
                    dataSource={predicao.legislacao_aplicavel}
                    renderItem={(leg) => (
                      <List.Item>
                        <List.Item.Meta
                          title={<Tag color="blue">{leg.tema?.toUpperCase() || ''}</Tag>}
                          description={
                            <>
                              <div><Text strong>Lei:</Text> {leg.lei}</div>
                              {leg.sumula_carf && (
                                <div>
                                  <Text strong>Súmula:</Text>{' '}
                                  <Tag color="purple">{leg.sumula_carf}</Tag>
                                </div>
                              )}
                              <div><Text type="secondary">{leg.observacao}</Text></div>
                            </>
                          }
                        />
                      </List.Item>
                    )}
                  />
                </Card>
              )}

              {/* Recomendação */}
              <Alert
                message="Recomendação"
                description={predicao.recomendacao}
                type={
                  predicao.predicao === 'favoravel' ? 'success' :
                  predicao.predicao === 'desfavoravel' ? 'warning' : 'info'
                }
                showIcon
                style={{ marginBottom: 24 }}
              />

              <Alert
                message="Disclaimer"
                description="Predição baseada em histórico de casos similares. Cada caso tem particularidades que podem alterar o resultado. Esta análise não substitui parecer jurídico."
                type="info"
                showIcon
              />
            </>
          )}

          {!predicao && !loadingPredicao && (
            <Empty description="Digite um tributo e uma tese para ver a predição" />
          )}
        </TabPane>
      </Tabs>
    </div>
  );
};

export default JuridicoPage;
